import { useErrorReporterFiltered } from "core/hooks/useErrorReporterFiltered";
import { TaskData } from "core/queries/GetTask";
import { useUpdateTask } from "core/queries/UpdateTask";
import { setArchivedTask, setUnscheduledTask } from "schedule/actions";
import { useStore as useScheduleStore } from "schedule/store";
import { normalizeType } from "tasks/helpers";
import { TaskStatus, TypeEnum } from "typings/globalGQLTypes";

export enum AdditionalOption {
  // UpdateTask: Set Status to Canceled
  Archive = "archive",
  // UpdateTask: Remove Assignee, Remove Due At / Scheduled Start At, set Status to Ready
  Unschedule = "unschedule",
}

export interface AdditionalOptionInterface {
  action?: () => void;
  description?: string;
  disabled: boolean;
  id: AdditionalOption;
  label: string;
}

export interface AdditionalOptionsObject {
  [AdditionalOption.Archive]: AdditionalOptionInterface | null;
  [AdditionalOption.Unschedule]: AdditionalOptionInterface | null;
}

export const getArchiveTaskOption = (
  task: TaskData,
  onArchiveTaskAction: () => void,
  onDeleteCleanAction: () => void,
): AdditionalOptionInterface | null => {
  const { status, type } = task;
  const normalizedTaskType = type && normalizeType(type);
  switch (normalizedTaskType) {
    case TypeEnum.Tom_Tasks_Clean:
    case TypeEnum.Tom_Tasks_SpaceClean:
      return {
        action: onDeleteCleanAction,
        description:
          "Completely remove the Clean, you will not be able to reschedule it.",
        // Cleans and Space Cleans cannot be Archived when in Progress / Completed / etc.
        disabled: status !== TaskStatus.ready,
        id: AdditionalOption.Archive,
        label: status === TaskStatus.canceled ? "Deleted" : "Delete Clean",
      };
    case TypeEnum.Tom_Tasks_IntrastayCheck:
    case TypeEnum.Tom_Tasks_QualityAudit:
      return {
        action: onArchiveTaskAction,
        description: "Archive the Task, you will be able to Unarchive it.",
        disabled: false,
        id: AdditionalOption.Archive,
        label:
          status === TaskStatus.canceled ? "Unarchive Task" : "Archive Task",
      };
    default:
      return null;
  }
};

export const getUnscheduleTaskOption = (
  task: TaskData,
  onUnscheduleAction: () => void,
): AdditionalOptionInterface | null => {
  const { dueAt, scheduledStartAt, status, type } = task;
  const normalizedTaskType = type && normalizeType(type);
  switch (normalizedTaskType) {
    case TypeEnum.Tom_Tasks_Clean:
    case TypeEnum.Tom_Tasks_SpaceClean:
      return {
        action: onUnscheduleAction,
        description:
          "Unschedule and unassign the Clean. You can reschedule it for today.",
        disabled:
          !scheduledStartAt ||
          !dueAt ||
          !!(
            status &&
            [
              TaskStatus.canceled,
              TaskStatus.complete,
              TaskStatus.deleted,
            ].includes(status as TaskStatus)
          ),
        id: AdditionalOption.Unschedule,
        label: "Unschedule Clean",
      };
    default:
      return null;
  }
};

export const useGetAdditionalOptions = (task: TaskData) => {
  const [onUpdateTask] = useUpdateTask({
    refetchQueries: [
      // Top Level Metrics for /schedule
      "GetSchedulerMetrics",
      // List of Cleans for /schedule
      "GetTaskSchedules",
      // List of Tasks for /tasks
      "GetTasks",
      // Task Details Panel
      "GetTask",
    ],
  });
  const errorReporter = useErrorReporterFiltered();
  const { dispatch: dispatchSchedule } = useScheduleStore();
  const onArchiveTaskAction = async () => {
    const nextStatus =
      task.status === TaskStatus.canceled
        ? TaskStatus.ready
        : TaskStatus.canceled;
    try {
      await onUpdateTask({
        variables: { taskId: task.id, status: nextStatus },
      });
    } catch (error) {
      errorReporter.notify(error);
    }
  };
  const onDeleteCleanAction = () => dispatchSchedule(setArchivedTask(task.id));
  const onUnscheduleCleanAction = () =>
    dispatchSchedule(setUnscheduledTask(task.id));
  return [
    getArchiveTaskOption(task, onArchiveTaskAction, onDeleteCleanAction),
    getUnscheduleTaskOption(task, onUnscheduleCleanAction),
  ].filter(x => !!x) as AdditionalOptionInterface[];
};

export const convertAdditionalOptionsArrayToObject = (
  additionalOptions: AdditionalOptionInterface[],
) =>
  additionalOptions.reduce(
    (acc, memo) => ({ ...acc, [memo.id]: memo }),
    {} as AdditionalOptionsObject,
  );
